import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createRangeSelection,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  KEY_DOWN_COMMAND,
  mergeRegister,
} from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';

import type { RefObject } from 'react';
import type { LexicalNode, TextNode } from 'lexical';
import type { SearchResponse } from './AddCatalogueLinkForm';

import { AddCatalogueLinkForm } from './AddCatalogueLinkForm';

import styles from 'components/RteEditor/RteEditor.module.css';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';
import {
  $getLinkTypeFromNode,
  CourseCatalogueNode,
  INSERT_COURSE_CATALOGUE_LINK,
  REMOVE_COURSE_CATALOGUE_LINK,
} from 'components/RteEditor/nodes/CourseCatalogueNode';
import { useOnClickOutside } from 'components/hooks/useOnClickOutside';

interface CourseCataloguePluginProps {
  onSearch: (query: string, linkType: 'course' | 'programme') => void;
  searchResponse: SearchResponse;
  parentScrollPosition: number;
}

export const CourseCataloguePlugin = ({
  onSearch,
  searchResponse,
  parentScrollPosition,
}: CourseCataloguePluginProps) => {
  const [editor] = useLexicalComposerContext();
  const t = useTranslation();
  const [courseIsActive, setCourseIsActive] = useState(false);
  const [programmeIsActive, setProgrammeIsActive] = useState(false);
  const courseButtonRef = useRef<HTMLButtonElement>(null);
  const programmeButtonRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number } | null>(null);
  const [type, setType] = useState<'course' | 'programme'>('course');
  const isVisibleRef = useRef(flyoutPosition !== null);

  const openFlyout = (type: 'course' | 'programme') => {
    setType(type);
    const rect =
      type === 'course'
        ? courseButtonRef.current?.getBoundingClientRect()
        : programmeButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setFlyoutPosition({ top: rect.bottom, left: rect.left });
    }
  };

  const closeFlyout = () => {
    onSearch('', type);
    setFlyoutPosition(null);
  };

  useEffect(() => {
    const removeKeyboardShortcut = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.altKey && event.key === 'C') {
          event.preventDefault();
          openFlyout('course');
          return true;
        }
        if (event.ctrlKey && event.shiftKey && event.altKey && event.key === 'P') {
          event.preventDefault();
          openFlyout('programme');
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    const removeStatusListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const offset = selection.anchor.offset;

          if (
            $isTextNode(anchorNode) &&
            offset === anchorNode.getTextContentSize() &&
            anchorNode.getNextSibling() instanceof CourseCatalogueNode
          ) {
            // Cursor staat aan het begin van de link!
            const courseCatalogueLink = anchorNode.getNextSibling();
            if (courseCatalogueLink) {
              const linkType = $getLinkTypeFromNode(courseCatalogueLink);
              setCourseIsActive(linkType === 'course');
              setProgrammeIsActive(linkType === 'programme');
              return;
            }
          }

          const courseCatalogueLink = $getNearestNodeOfType(anchorNode, CourseCatalogueNode);
          if (courseCatalogueLink) {
            const linkType = $getLinkTypeFromNode(courseCatalogueLink);
            setCourseIsActive(linkType === 'course');
            setProgrammeIsActive(linkType === 'programme');
            return;
          }
          setCourseIsActive(false);
          setProgrammeIsActive(false);
        }
      });
    });

    const moveToEndOfWhiteSpace = (whiteSpace: TextNode) => {
      const selection = $createRangeSelection();
      const endOffset = whiteSpace.getTextContentSize();
      selection.setTextNodeRange(whiteSpace, endOffset, whiteSpace, endOffset);
      $setSelection(selection);
    };

    const isCursorBeforeLink = (anchorNode: LexicalNode, offset: number) =>
      $isTextNode(anchorNode) &&
      offset === anchorNode.getTextContentSize() &&
      anchorNode.getNextSibling() instanceof CourseCatalogueNode;

    const removeUnEditable = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const key = event.key;
          const anchorNode = selection.anchor.getNode();
          const offset = selection.anchor.offset;
          let courseCatalogueLink = $getNearestNodeOfType(anchorNode, CourseCatalogueNode);

          // Detect if cursor is right before the link
          if (isCursorBeforeLink(anchorNode, offset)) {
            courseCatalogueLink = anchorNode.getNextSibling();
          }

          if (!courseCatalogueLink) {
            return false;
          }

          const nodeLength = courseCatalogueLink?.getTextContentSize();

          // Prevent Enter if in the middle of the link, allow only at start or end
          if (courseCatalogueLink && event.key === 'Enter') {
            event.preventDefault();
            editor.update(() => {
              if (isCursorBeforeLink(anchorNode, offset)) {
                const brNode = $createTextNode('\n');
                courseCatalogueLink.insertBefore(brNode);
              }
              if (offset === nodeLength) {
                const brNode = $createTextNode('\n');
                courseCatalogueLink.insertAfter(brNode);
                moveToEndOfWhiteSpace(brNode);
              }
            });
            return true;
          }

          // Allow add space before or after the link
          if (courseCatalogueLink && (key === ' ' || event.code === 'Space')) {
            event.preventDefault();
            editor.update(() => {
              const spaceToAdd = $createTextNode(' ');
              if (isCursorBeforeLink(anchorNode, offset)) {
                courseCatalogueLink.insertBefore(spaceToAdd);
                moveToEndOfWhiteSpace(spaceToAdd);
              }
              if (offset === nodeLength) {
                courseCatalogueLink.insertAfter(spaceToAdd);
                moveToEndOfWhiteSpace(spaceToAdd);
              }
            });
            return true;
          }

          // Delete link if selected and Backspace or Del is pressed
          if (courseCatalogueLink && (key === 'Backspace' || key === 'Delete')) {
            event.preventDefault();
            editor.update(
              () => {
                editor.dispatchCommand(REMOVE_COURSE_CATALOGUE_LINK, undefined);
              },
              { discrete: true }
            );
            return true;
          }

          // Allow Home, End and Arrow navigation
          if (
            courseCatalogueLink &&
            key !== 'ArrowLeft' &&
            key !== 'ArrowRight' &&
            key !== 'ArrowUp' &&
            key !== 'ArrowDown' &&
            key !== 'Home' &&
            key !== 'End' &&
            !(event.ctrlKey && (key.toLowerCase() === 'z' || key.toLowerCase() === 'y')) &&
            !(event.ctrlKey && key.toLowerCase() === 'c')
          ) {
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    const removeCopy = editor.registerCommand(
      COPY_COMMAND,
      (event: ClipboardEvent) => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const courseCatalogueLink = $getNearestNodeOfType(anchorNode, CourseCatalogueNode);
          if (courseCatalogueLink) {
            event.preventDefault();
            const domElement = editor.getElementByKey(courseCatalogueLink.getKey());
            if (domElement) {
              event.clipboardData?.setData('text/html', domElement.outerHTML);
              event.clipboardData?.setData('text/plain', courseCatalogueLink.getTextContent());
            }
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
    return () => {
      removeKeyboardShortcut();
      removeStatusListener();
      removeUnEditable();
      removeCopy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useOnClickOutside(flyoutRef as RefObject<HTMLElement>, () => setFlyoutPosition(null));

  const handleOnScroll = () => {
    closeFlyout();
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event: KeyboardEvent) => {
          if (event.key === 'Escape' && isVisibleRef.current) {
            event.preventDefault();
            closeFlyout();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),

      editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          prevRootElement.removeEventListener('scroll', handleOnScroll);
        }

        if (rootElement) {
          rootElement.addEventListener('scroll', handleOnScroll);
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    if (flyoutPosition) {
      closeFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentScrollPosition]);

  return (
    <div className={styles['editor__toolbar-button-group']}>
      <div className={styles['editor__toolbar-button-flyoutwrapper']}>
        <div className={styles['editor__toolbar-button-group--text']}>
          <button
            onClick={() => openFlyout('course')}
            title={t.cataloguelinks.courseTooltip}
            className={clsx(
              styles['editor__toolbar-button'],
              styles['editor__toolbar-button--text'],
              courseIsActive && styles[`editor__toolbar-button--active`]
            )}
            aria-label={t.aria.labels.courselink}
            aria-keyshortcuts={t.aria.keyshortcuts.courselink}
            type="button"
            ref={courseButtonRef}
          >
            {t.cataloguelinks.courseButtonLabel}
          </button>
          <button
            onClick={() => openFlyout('programme')}
            title={t.cataloguelinks.programmeTooltip}
            className={clsx(
              styles['editor__toolbar-button'],
              styles['editor__toolbar-button--text'],
              programmeIsActive && styles[`editor__toolbar-button--active`]
            )}
            aria-label={t.aria.labels.programmelink}
            aria-keyshortcuts={t.aria.keyshortcuts.programmelink}
            type="button"
            ref={programmeButtonRef}
          >
            {t.cataloguelinks.programmeButtonLabel}
          </button>
        </div>
        {flyoutPosition && (
          <div
            className={styles['editor__toolbar-flyout-wrapper']}
            style={{ top: flyoutPosition.top, left: flyoutPosition.left }}
            ref={flyoutRef}
          >
            <AddCatalogueLinkForm
              linkType={type}
              onCloseHandler={closeFlyout}
              onSearchHandler={onSearch}
              searchResponse={searchResponse}
              onSelect={(code, label, linkType) => {
                editor.dispatchCommand(INSERT_COURSE_CATALOGUE_LINK, {
                  linkType,
                  code,
                  label,
                });
                closeFlyout();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
